import { WX_ID_LIVECAMERA } from "../../../store/WeatherContentsState";
import WRAP from "WRAP-JS";
import { configLiveCameraDh, configLiveCameraLayer } from "./config/ConfigLiveCamera";
import { convertWRAPPoint, isUtc, language } from "../../../utils/FPEUtils";
import { COMMUNICATE_ERROR_MESSAGE_SELECTABLE_LIST_CAMERA_MASTER, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD } from "../../../constants/ErrorMessage";

class LiveCamera {
  static NAME = WX_ID_LIVECAMERA;

  static MIN_SIZE_X = 100;
  static MIN_SIZE_Y = 100;

  dh;
  layer;

  ready = false;
  visible;
  dispatcher;

  thumbnail = false;
  // fitMode = 'latlon';
  fitMode = 'screen';

  constructor(contract) {
    this.layer = new WRAP.Geo.Layer(LiveCamera.NAME);
    this.layer.setVisible(false);
    this.contract = contract;
    this._queue = new Set();
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  initialize() {
    this.initData();
    this.initLayer();
    this.ready = true;
  }

  initData() {
    this.dh = new WRAP.Data(configLiveCameraDh);
    this.dh.set('CameraMaster',{
      "contract_id": this.contract
    })
    this.dh.updated('CameraMaster', null, () => {
      this.dispatcher({type: 'SHOW_ERROR', payload: {
        message: COMMUNICATE_ERROR_MESSAGE_SELECTABLE_LIST_CAMERA_MASTER,
        additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
      }});
    });
  }

  initLayer() {
    if (isUtc()) {
      configLiveCameraLayer.Attributes.date_format = "%month%/%day% %0hour%:%0min% UTC";
    }
    this.layer.configure(this.dh, configLiveCameraLayer);

    this.layer._renderer.inspect(this._layerInspector);

    if (this.thumbnail) {
      this.setThumbnail(true);
    }
  }
  drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return this.layer;
  }

  setVisible(visible) {
    if (!this.ready) return;

    if (visible) {
      this.layer.show();
    } else {
      this.layer.hide();
    }
  }

  getLegend() {
    return {
      id: LiveCamera.NAME,
      label: 'Live Camera'
    }
  }

  getThumbnail() {
    return this.thumbnail;
  }

  setThumbnail(visible) {
    this.thumbnail = visible;
    this.layer.setAttributes({
      thumbnail_icon: visible ? {width: 36, height: 36, round_corner: 8} : undefined
    })
  }

  changeLanguage() {
    const l = language();
    if (this.visible) {
      let nameType;
      if (l === 'ja') {
        nameType = 'LNAME';
      } else {
        nameType = 'ENAME';
      }
      Object.values(this.layer._renderer.camera_info).forEach(c=>{
        c.name = nameType;
      });
      this.layer.invalidate();
    }
  }

  getExtendedOperation() {
    if (!this.layer._renderer || !this.layer._renderer.camera_window) {
      return;
    }
    const cameras = this.layer._renderer.camera_window.filter(w => w.visible && !w.tooltip).reduce((previous, w) => {
      previous[w.camera.id] = {
        id: w.camera.id,
        dispLon: w.window.point.lonDegree(),
        dispLat: w.window.point.latDegree(),
        dispTop: parseFloat(w.window.style.top),
        dispLeft: parseFloat(w.window.style.left),
        small: w.small,
      }
      return previous;
    }, {});
    return {
      camera: {
        function: "setInitCameras",
        value: cameras
      }
    };
  }


  async setInitCameras(cameras) {
    this.initCameras = Object.assign({}, cameras);
    // Contents Set対応。描画済みのカメラを消して、this.initCamerasの情報で再描画
    if (this.layer._renderer !== undefined) {
      const overlay = this.layer._renderer.getOverlay();
      if (overlay) {
        overlay.clearWindow();
        if (Object.keys(this.initCameras).length > 0) {
          const drawInitCamera = () => {
            Object.keys(this.layer._renderer.camera_info).forEach(c => {
              this._drawInitCamera({
                camera: c,
                type: "CameraInfoResponse"
              });
            });
          }

          if (this.layer.visible()) {
            drawInitCamera();
          } else {
            // layer非表示の状態ではVisibilityChangeイベントが発火した後でないと、描画できないため待ち受ける
            this._queue.add(drawInitCamera);
          }
        }
      }
    }
  }

  changeTimeZone(tz) {
    let attr;
    if (configLiveCameraLayer.Attributes.timezone && configLiveCameraLayer.Attributes.timezone[tz]) {
      attr = configLiveCameraLayer.Attributes.timezone[tz];
    } else {
      attr = {};
    }
    this.layer.setAttributes(attr);
  }

  setFilterCameraGroup(filterCameraGroup) {
    this.filterCameraGroup = filterCameraGroup;
    if (!this.ready) return;

    if (this.filterCameraGroup) {
      //カメラアイコンをフィルタリング
      Object.keys(this.layer._renderer.camera_info).forEach(area => {
        const info = this.layer._renderer.camera_info[area];
        info.icon_visible = this._isFilterPassing(area);
      });
      //カメラウィンドウをフィルタリング
      this.layer._renderer.camera_window?.forEach(win => {
        if (win.visible) {
          if (!this._isFilterPassing(win.camera.id)) {
            win.hide();
          }
        }
      });
    } else {
      Object.values(this.layer._renderer.camera_info).forEach(c => {
        c.icon_visible = true;
      });
    }
    this.layer.invalidate();
  }

  _isFilterPassing(area) {
    if (this.filterCameraGroup) {
      if (this.filterCameraGroup.filterType === 'category') {
        const c = this.layer._renderer.camera_info[area];
        return this.filterCameraGroup.category.includes(c.config.category);
      } else if (this.filterCameraGroup.filterType === 'area') {
        return this.filterCameraGroup.areaList.includes(area);
      }
      return true;
    }
    return true
  }

  _layerInspector = _ins => {
    if (_ins.type === 'CameraInfoResponse') {
      this._drawInitCamera(_ins);
    } else if (_ins.type === 'LayerShow') {
      // initCamera描画可能状態になったので（queueにつまっていたdrawInitCameraを実行）
      for (const fn of this._queue) {
        fn();
      }
      this._queue.clear();
    }
  }

  _drawInitCamera = _ins => {
    let cameraVisible = true;
    if (this.filterCameraGroup) {
      cameraVisible = this._isFilterPassing(_ins.camera);
      const c = this.layer._renderer.camera_info[_ins.camera];
      if (c) {
        c.icon_visible = cameraVisible;
      }
    }
    if (this.initCameras) {
      if (_ins.camera in this.initCameras) {
        if (!cameraVisible) {
          delete this.initCameras[_ins.camera];
          return;
        }
        const w = this.initCameras[_ins.camera];
        this.layer._renderer.showCameraWindow(this.layer._renderer.camera_info[w.id], this.layer);
        const current_camera = this.layer._renderer.currentCamera();
        current_camera.window.controller.pinned = true;
        if (this.fitMode === 'screen') {
          const {width: screenWidth, height: screenHeight} = WRAP.Geo.getSize();
          let left = w.dispLeft;
          let top = w.dispTop;
          if (screenWidth - w.dispLeft < this.constructor.MIN_SIZE_X) {
            left = screenWidth - this.constructor.MIN_SIZE_X;
          }
          if (screenHeight - w.dispTop < this.constructor.MIN_SIZE_Y) {
            top = screenHeight - this.constructor.MIN_SIZE_Y;
          }
          const sp = new WRAP.Geo.ScreenPoint(left, top);
          const point = WRAP.Geo.getPoint(sp);
          current_camera.window.point = point;
          current_camera.window.style.left = left + 'px';
          current_camera.window.style.top = top + 'px';
        } else if (this.fitMode === 'latlon') {
          const point = convertWRAPPoint(w.dispLat, w.dispLon);
          const sp = WRAP.Geo.getScreenPoint(point);
          current_camera.window.point = point;
          current_camera.window.style.left = sp.x + 'px';
          current_camera.window.style.top = sp.y + 'px';
        }
        if (current_camera.tooltip) {
          current_camera.tooltip = false;
        }
        current_camera.smooth = true;
        this.layer._renderer.update(w.small ? 'WindowMinimize' : 'WindowMaximize', w.id);
        current_camera.small = w.small;
        current_camera.set();
        // setTimeout(() => {
        current_camera.updateImageStatus();
        // }, 1);
        delete this.initCameras[_ins.camera];
      }
    }
  }
}

export default LiveCamera;
